import * as React from 'react'

import type { Field } from 'factorial-form'
import compact from 'lodash/compact'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import UserSelector from 'modules/core/components/UserSelector'
import usersCollection from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import employees from 'modules/employees/store/collections/employees'

interface Props {
  disabled?: boolean
  field: Field | undefined
  users?: User[]
  multi?: boolean
  useEmployee?: boolean
}

const employeeId = (accessId: number) => usersCollection.get(accessId)?.employee?.id

const formMultipleValue = (selectedValues?: number[], useEmployee?: boolean) => {
  return selectedValues?.map((value) => (useEmployee ? employeeId(value) : value))
}

const formValue = (
  multi: boolean,
  id: number,
  useEmployee?: boolean,
  selectedValues?: number[]
) => {
  if (multi) {
    return formMultipleValue(selectedValues, useEmployee)
  }
  return useEmployee ? employeeId(id) : id
}

const accessId = (employeeId: number) => employees.get(employeeId)?.attributes.access_id

const buildDefaultSelectedIds = (
  field: Field | undefined,
  multi: boolean,
  useEmployee?: boolean
) => {
  const defaultSelectedIds = multi
    ? ((field?.value ? field?.value : []) as number[])
    : [Number(field?.value)]
  return defaultSelectedIds.map((id) => (useEmployee ? accessId(id) : id))
}

export const UserSelect = observer(
  ({
    field,
    disabled = false,
    users = usersCollection.models,
    multi = false,
    useEmployee,
  }: Props) => {
    const defaultSelectedIds = buildDefaultSelectedIds(field, multi, useEmployee)
    const [selectedIds, setSelected] = React.useState(compact(defaultSelectedIds))

    const onSelect = (id: number | null | undefined) => {
      const selectedValues = multi ? xor(selectedIds, compact([id])) : compact([id])
      setSelected(selectedValues)
      field?.set(formValue(multi, id!, useEmployee, selectedValues))
    }

    const onSelectMultiple = (accessIds: number[]) => {
      setSelected(accessIds)

      field?.set(formMultipleValue(accessIds, useEmployee))
    }

    return (
      <UserSelector
        users={users}
        disabled={disabled}
        onSelect={onSelect}
        selectedIds={selectedIds}
        onSelectMultiple={multi ? onSelectMultiple : undefined}
      />
    )
  }
)
